import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

interface ServiceArea {
  city: string;
  state: string;
}

interface PaginatedServiceAreas {
  count?: number;
  results: ServiceArea[];
}

type ServiceAreasResponse = ServiceArea[] | PaginatedServiceAreas;

interface OrganizationDefaults {
  name: string;
  phone?: string;
  email?: string;
  address?: string;
  website?: string;
  description?: string;
  services: string[];
  serviceAreas: ServiceArea[];
  brandColors?: {
    primary?: string;
    secondary?: string;
  };
  businessType?: 'residential' | 'commercial' | 'both';
  yearsInBusiness?: number;
  teamSize?: number;
  certifications?: string[];
  specialties?: string[];
  businessHours?: string;
}

export const useOrganizationDefaults = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['organization-defaults'],
    queryFn: async () => {
      try {
        // Fetch all data in parallel
        const [overviewResponse, whoamiResponse, areasResponse, orgResponse] = await Promise.all([
          api.get<any>('/api/marketing/organization-overview/').catch(() => ({ data: {} })),
          api.get<any>('/api/whoami/').catch(() => ({ data: {} })),
          api.get<ServiceAreasResponse>('/api/areas-served/').catch(() => ({ data: [] as ServiceArea[] })),
          api.get<any>('/api/organizations/current/').catch(() => ({ data: {} })),
        ]);

        const overview = overviewResponse.data || {};
        const whoami = whoamiResponse.data || {};
        const org = whoami.organization || {};
        // Handle paginated response: { count, results: [...] } or flat array
        const areasData = areasResponse.data;
        const areasServed = Array.isArray(areasData)
          ? areasData
          : (areasData?.results || []);
        const orgDetails = orgResponse.data || {};

        // Construct full address from individual fields
        const addressParts = [
          orgDetails.main_office_address_1,
          orgDetails.main_office_city,
          orgDetails.main_office_state,
          orgDetails.main_office_zip,
        ].filter(Boolean);
        const fullAddress = addressParts.length >= 3
          ? `${orgDetails.main_office_address_1 || ''}, ${orgDetails.main_office_city || ''}, ${orgDetails.main_office_state || ''} ${orgDetails.main_office_zip || ''}`.trim()
          : '';

        // Format business hours from JSON
        const formatBusinessHours = (hours: any): string => {
          if (!hours) return '';
          if (typeof hours === 'string') return hours;
          if (typeof hours === 'object') {
            const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
            const lines: string[] = [];
            for (const day of dayOrder) {
              if (hours[day]) {
                const dayName = day.charAt(0).toUpperCase() + day.slice(1);
                lines.push(`${dayName}: ${hours[day]}`);
              }
            }
            return lines.join('\n');
          }
          return '';
        };

        // Extract and format data
        const defaults: OrganizationDefaults = {
          name: org.name || overview.company_name || whoami.organization_name || '',
          phone: orgDetails.company_phone_number || orgDetails.phone || org.phone || overview.phone || '',
          email: orgDetails.company_email || orgDetails.email || org.email || overview.email || whoami.email || '',
          address: fullAddress || orgDetails.address || org.address || overview.address || '',
          website: orgDetails.website || org.website || overview.website || '',
          description: overview.company_overview || overview.overview || '',
          services: [],
          serviceAreas: areasServed.map((area: ServiceArea) => ({ city: area.city, state: area.state })),
          brandColors: {
            primary: overview.brand_color_1 ? `#${overview.brand_color_1}` : undefined,
            secondary: overview.brand_color_2 ? `#${overview.brand_color_2}` : undefined,
          },
          businessType: orgDetails.residential_commercial_both || 'residential',
          yearsInBusiness: orgDetails.years_in_business || undefined,
          teamSize: orgDetails.team_size || org.team_size,
          certifications: [],
          specialties: [],
          businessHours: formatBusinessHours(orgDetails.hours_of_operation),
        };

        // Extract services from description if available
        const overviewText = overview.company_overview || overview.overview || '';
        if (overviewText) {
          const commonServices = [
            'Roof Repair',
            'Roof Replacement',
            'Emergency Services',
            'Roof Inspection',
            'Gutter Installation',
            'Storm Damage Repair',
            'Commercial Roofing',
            'Residential Roofing',
            'Metal Roofing',
            'Shingle Roofing',
            'Flat Roofing',
            'Roof Maintenance',
          ];

          const descLower = overviewText.toLowerCase();
          defaults.services = commonServices.filter(service =>
            descLower.includes(service.toLowerCase())
          );

          // If no services found in description, add common ones
          if (defaults.services.length === 0) {
            defaults.services = [
              'Roof Repair',
              'Roof Replacement',
              'Roof Inspection',
              'Emergency Services'
            ];
          }
        }

        return defaults;
      } catch (error) {
        // Return empty defaults on error
        return {
          name: '',
          services: [],
          serviceAreas: [],
        } as OrganizationDefaults;
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return {
    defaults: data,
    isLoading,
    error,
  };
};
